import {
  createContext,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision'
import { Box, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { uploadCandidateProfilePicture } from '../axios/api/candidate'
import { saveCalibrationDetailsToDB } from '../axios/api/proctor'
import CustomDialog from '../components/dialog'
import MeetingVideoPlayer from '../components/meeting-video-player'
import { NO_FACE_DETECTION_BLOCK_UI_IN_MINS } from '../env/env'
import { getUserDetails, showErrorToast } from '../utils/utils'

export const MediapipeContext = createContext(null)

const CENTER_TOLERANCE = 0.3
const MIN_FACE_SIZE = 0.03
const YAW_THRESHOLD = 8
const ROLL_THRESHOLD = 8
const CALIBRATION_FRAMES_NEEDED = 1
const SMOOTHING_ALPHA = 0.3

export const CalibrationView = memo(
  ({
    video,
    showButton,
    frameStatistics,
    isMediaPipeReady,
    isMediaPipeLoading,
    videoEleId = 'calibration-video-feed',
    videoStyle = {}
  }) => {
    const [isVideoElementReady, setIsVideoElementReady] = useState(false)

    useEffect(() => {
      let timerId = null
      let cancelled = false

      const checkVideoReady = () => {
        if (cancelled) return
        const videoEl = document.getElementById(videoEleId)
        if (videoEl && videoEl.readyState >= 2 && videoEl.videoWidth > 0) {
          setIsVideoElementReady(true)
        } else {
          timerId = setTimeout(checkVideoReady, 200)
        }
      }

      checkVideoReady()

      return () => {
        cancelled = true
        if (timerId) clearTimeout(timerId)
      }
    }, [videoEleId])

    const isVideoLoading = !isVideoElementReady
    const isInitializing = isVideoLoading || !isMediaPipeReady

    const getLoadingMessage = () => {
      if (!isMediaPipeReady && isMediaPipeLoading) {
        return 'Loading face detection models... This may take a moment on slow networks.'
      }
      if (!isMediaPipeReady && !isMediaPipeLoading) {
        return 'Preparing face detection...'
      }
      if (isVideoLoading) {
        return 'Initializing camera...'
      }
      return 'Initializing...'
    }

    return (
      <Box sx={{ gap: '16px', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ position: 'relative' }}>
          <MeetingVideoPlayer
            videoStream={video}
            videoEleId={videoEleId}
            videoStyle={videoStyle}
          />
          {/* Face guide overlay */}
          {!showButton && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 5
              }}
            >
              <svg
                width="40%"
                height="65%"
                viewBox="0 0 200 280"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ overflow: 'visible' }}
              >
                <ellipse
                  cx="100"
                  cy="140"
                  rx="85"
                  ry="125"
                  stroke="rgba(255, 255, 255, 0.7)"
                  strokeWidth="3"
                  strokeDasharray="12 6"
                  fill="none"
                >
                  <animate
                    attributeName="stroke-opacity"
                    values="0.4;0.9;0.4"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </ellipse>
                {/* Small crosshair lines at center */}
                <line
                  x1="92"
                  y1="140"
                  x2="108"
                  y2="140"
                  stroke="rgba(255,255,255,0.5)"
                  strokeWidth="1.5"
                />
                <line
                  x1="100"
                  y1="132"
                  x2="100"
                  y2="148"
                  stroke="rgba(255,255,255,0.5)"
                  strokeWidth="1.5"
                />
              </svg>
              <Typography
                sx={{
                  mt: 1,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  borderRadius: '4px',
                  textAlign: 'center'
                }}
              >
                Place your face inside the oval
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ minHeight: '48px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          {isInitializing ? (
            <Typography sx={{ fontWeight: '600' }} color={'info.main'}>
              {getLoadingMessage()}
            </Typography>
          ) : (
            <>
              {frameStatistics?.inPitch == null &&
                frameStatistics?.inRoll == null &&
                frameStatistics?.inYaw == null && (
                  <Typography sx={{ fontWeight: '600' }} color={'error'}>
                    {`Face not detected. Please make sure the camera shutter is open and your face is centered in the frame.`}
                  </Typography>
                )}
              {!showButton && (
                <>
                  {(frameStatistics?.inRoll < -ROLL_THRESHOLD ||
                    frameStatistics?.inRoll > ROLL_THRESHOLD) && (
                    <Typography sx={{ fontWeight: '600' }} color={'error'}>
                      {`Tilt your face towards ${
                        frameStatistics?.inRoll < 0 ? 'left' : 'right'
                      } to calibrate`}
                    </Typography>
                  )}
                  {(frameStatistics?.inYaw < -YAW_THRESHOLD ||
                    frameStatistics?.inYaw > YAW_THRESHOLD) && (
                    <Typography sx={{ fontWeight: '600' }} color={'error'}>
                      {`Turn your face towards ${
                        frameStatistics?.inYaw > 0 ? 'left' : 'right'
                      } to calibrate`}
                    </Typography>
                  )}
                  {/* Show "stay steady" when face is in position */}
                  {frameStatistics?.inRoll != null &&
                    frameStatistics?.inYaw != null &&
                    Math.abs(frameStatistics.inRoll) <= ROLL_THRESHOLD &&
                    Math.abs(frameStatistics.inYaw) <= YAW_THRESHOLD && (
                      <Typography
                        sx={{ fontWeight: '600' }}
                        color={'success.main'}
                      >
                        Stay steady, calibrating...
                      </Typography>
                    )}
                </>
              )}
              {showButton && (
                <Typography sx={{ fontWeight: '600' }} color={'success.main'}>
                  Calibrating, please wait...
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    )
  }
)

const CalibrationModal = memo(
  ({
    open,
    video,
    title,
    showButton,
    frameStatistics,
    isMediaPipeReady,
    isMediaPipeLoading
  }) => {
    if (!open) return null

    return (
      <CustomDialog
        open={open}
        outsideClikClose={false}
        title={title || 'Calibrate Camera'}
        dialogPaperStyle={{ '& .MuiDialogTitle-root': { padding: '1rem' } }}
      >
        <CalibrationView
          video={video}
          showButton={showButton}
          frameStatistics={frameStatistics}
          isMediaPipeReady={isMediaPipeReady}
          isMediaPipeLoading={isMediaPipeLoading}
        />
      </CustomDialog>
    )
  }
)

const MediapipeProvider = ({ children }) => {
  const stageDetails = useSelector(
    (state) => state.candidate.contestStageDetails
  )
  const userDetails = useSelector((state) => state.auth.userData)

  const videoRef = useRef(null)
  const noFaceDetectedCount = useRef(0)
  const isScriptLoaded = useRef(false)
  const faceDetectorRef = useRef(null)
  const isProcessingRef = useRef(false)
  const lastFaceDetectionTimeRef = useRef(0)
  const hiddenCanvasRef = useRef(null)
  const mediaPipeReadyRef = useRef(false)
  const goodFrameCountRef = useRef(0)
  const smoothedYawRef = useRef(null)
  const smoothedRollRef = useRef(null)
  const calibrationInProgressRef = useRef(false)

  const [showCanvas, setShowCanvas] = useState({
    webcam: false,
    screenShare: false
  })
  const [calibrationDetails, setCalibrationDetails] = useState(null)
  const [mainFrame, setMainFrame] = useState({
    data: [],
    inRoll: null,
    inPitch: null,
    inYaw: null
  })
  const [scriptLoaded, setScriptLoaded] = useState({
    cvLoaded: true,
    mediaPipeLoaded: false
  })
  const [calibrationModal, setCalibrationModal] = useState({
    open: false,
    title: null,
    showButton: false,
    inlineActive: false
  })
  const [isMediaPipeLoading, setIsMediaPipeLoading] = useState(false)
  const [isMediaPipeReadyState, setIsMediaPipeReadyState] = useState(false)

  /** Estimate yaw from nose-to-ear distance ratio. Keypoints: 0=rightEye, 1=leftEye, 2=noseTip, 3=mouth, 4=rightEar, 5=leftEar */
  const estimateYaw = useCallback(
    (keypoints, boundingBox, frameWidth, frameHeight) => {
      if (!keypoints || keypoints.length < 6) return 0

      const noseTip = keypoints[2]
      const rightEar = keypoints[4]
      const leftEar = keypoints[5]

      const distToRight = Math.abs(noseTip.x - rightEar.x)
      const distToLeft = Math.abs(noseTip.x - leftEar.x)

      if (distToRight + distToLeft === 0) return 0

      const ratio = distToRight / (distToRight + distToLeft)

      // Scale yaw multiplier by face size: large face (close) → 60°, small face (far) → 40°
      let yawMultiplier = 60
      if (boundingBox && frameWidth && frameHeight) {
        const faceRatio =
          (boundingBox.width * boundingBox.height) / (frameWidth * frameHeight)
        // faceRatio ~0.25 = very close, ~0.03 = far away
        const scale = Math.min(
          1,
          Math.max(0, (faceRatio - 0.03) / (0.25 - 0.03))
        )
        yawMultiplier = 40 + scale * 20 // ranges from 40 (far) to 60 (close)
      }

      const rawYaw = (ratio - 0.5) * yawMultiplier

      // Apply EMA smoothing
      if (smoothedYawRef.current === null) {
        smoothedYawRef.current = rawYaw
      } else {
        smoothedYawRef.current =
          SMOOTHING_ALPHA * rawYaw +
          (1 - SMOOTHING_ALPHA) * smoothedYawRef.current
      }

      return parseFloat(smoothedYawRef.current.toFixed(2))
    },
    []
  )

  /** Estimate roll (head tilt) from the angle between eye keypoints */
  const estimateRoll = useCallback((keypoints) => {
    if (!keypoints || keypoints.length < 2) return 0

    const rightEye = keypoints[0]
    const leftEye = keypoints[1]

    const dx = leftEye.x - rightEye.x
    const dy = leftEye.y - rightEye.y

    const rawRoll = Math.atan2(dy, dx) * (180 / Math.PI)

    // Apply EMA smoothing
    if (smoothedRollRef.current === null) {
      smoothedRollRef.current = rawRoll
    } else {
      smoothedRollRef.current =
        SMOOTHING_ALPHA * rawRoll +
        (1 - SMOOTHING_ALPHA) * smoothedRollRef.current
    }

    return parseFloat(smoothedRollRef.current.toFixed(2))
  }, [])

  const isFaceCentered = useCallback((boundingBox, frameWidth, frameHeight) => {
    const faceCenterX =
      (boundingBox.originX + boundingBox.width / 2) / frameWidth
    const faceCenterY =
      (boundingBox.originY + boundingBox.height / 2) / frameHeight

    const isCenteredX = Math.abs(faceCenterX - 0.5) < CENTER_TOLERANCE
    const isCenteredY = Math.abs(faceCenterY - 0.5) < CENTER_TOLERANCE

    const faceRatio =
      (boundingBox.width * boundingBox.height) / (frameWidth * frameHeight)
    const isLargeEnough = faceRatio > MIN_FACE_SIZE

    return isCenteredX && isCenteredY && isLargeEnough
  }, [])

  const saveCalibrationDetails = useCallback(async (payload) => {
    const response = await saveCalibrationDetailsToDB(payload)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      toast.success('Calibrated Successfully')
    }
  }, [])

  const initializeFaceDetector = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 3

    setIsMediaPipeLoading(true)

    try {
      const filesetResolver = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm'
      )

      const detector = await FaceDetector.createFromOptions(filesetResolver, {
        baseOptions: {
          modelAssetPath:
            'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        minDetectionConfidence: 0.3
      })

      faceDetectorRef.current = detector
      isScriptLoaded.current = true

      setScriptLoaded({ cvLoaded: true, mediaPipeLoaded: true })
      setIsMediaPipeLoading(false)

      setTimeout(() => {
        mediaPipeReadyRef.current = true
        setIsMediaPipeReadyState(true)
      }, 500)
    } catch (error) {
      console.error('MediaPipe initialization failed:', error)

      if (retryCount < MAX_RETRIES) {
        const delay = Math.pow(2, retryCount + 1) * 1000
        setTimeout(() => {
          initializeFaceDetector(retryCount + 1)
        }, delay)
      } else {
        setScriptLoaded((prev) => ({ ...prev, mediaPipeLoaded: false }))
        setIsMediaPipeLoading(false)
        mediaPipeReadyRef.current = false
        setIsMediaPipeReadyState(false)
        toast.error(
          'Failed to load face detection. Please refresh the page and try again.'
        )
      }
    }
  }, [])

  const captureImageFrame = useCallback((videoElement) => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    canvas.width = videoElement.videoWidth
    canvas.height = videoElement.videoHeight
    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height)

    const dataUrl = canvas.toDataURL('image/jpeg')

    canvas.width = 0
    canvas.height = 0

    return dataUrl
  }, [])

  const base64ToFormData = useCallback(async (base64) => {
    const [header, base64Data] = base64.split(',')
    const mimeTypeMatch = header.match(/data:(.*);base64/)
    const mimeType = mimeTypeMatch
      ? mimeTypeMatch[1]
      : 'application/octet-stream'

    const binary = atob(base64Data)
    const binaryLen = binary.length
    const bytes = new Uint8Array(binaryLen)

    for (let i = 0; i < binaryLen; i++) {
      bytes[i] = binary.charCodeAt(i)
    }

    const blob = new Blob([bytes], { type: mimeType })
    const formData = new FormData()
    formData.append(
      'file',
      blob,
      `${getUserDetails()?.sub || 'candidate-email'}.jpeg`
    )

    return formData
  }, [])

  const intialiseMediapipe = useCallback(() => {
    if (!isScriptLoaded.current) {
      initializeFaceDetector()
    }
  }, [initializeFaceDetector])

  const intialiseVideoElement = useCallback((stream) => {
    videoRef.current = stream
  }, [])

  const handleCalibrateCamera = useCallback(
    async (frameData) => {
      if (calibrationInProgressRef.current) return
      calibrationInProgressRef.current = true

      try {
        const { rollValue, pitchValue, yawValue, videoElement, overrideContestId } = frameData

        const imgSrc = captureImageFrame(videoElement)

        const movementDetections = {
          calibratedFrameDetails: {
            inRoll: rollValue,
            inPitch: pitchValue,
            inYaw: yawValue,
            data: [rollValue, pitchValue, yawValue]
          },
          imgSrc
        }

        await saveCalibrationDetails({
          contestId: overrideContestId || stageDetails?.contestId,
          candidateId: userDetails?.id,
          movementDetections
        })

        if (window.eventEmitter) {
          window.eventEmitter.emit('webcamCalibrated', movementDetections)
        }

        if (imgSrc) {
          const payload = await base64ToFormData(imgSrc)
          await uploadCandidateProfilePicture(payload)
        }

        setCalibrationModal({ open: false, title: null, showButton: false, inlineActive: false })
      } finally {
        calibrationInProgressRef.current = false
      }
    },
    [
      userDetails?.id,
      captureImageFrame,
      saveCalibrationDetails,
      stageDetails?.contestId,
      base64ToFormData
    ]
  )

  const predictWebcam = useCallback(
    async (showCalibration, inline = false, overrideContestId = null) => {
      const now = performance.now()

      if (showCalibration) {
        isProcessingRef.current = false
        setCalibrationModal((prev) => {
          if (!prev.open && !inline) {
            goodFrameCountRef.current = 0
            setMainFrame({ data: [], inRoll: null, inPitch: null, inYaw: null })
            return { ...prev, open: true, showButton: false }
          }
          if (inline && !prev.inlineActive) {
            goodFrameCountRef.current = 0
            setMainFrame({ data: [], inRoll: null, inPitch: null, inYaw: null })
            return { ...prev, inlineActive: true, showButton: false }
          }
          return prev
        })
      }

      const videoEle = showCalibration
        ? document.getElementById('calibration-video-feed')
        : document.getElementById('webcam-video-feed')

      if (!showCalibration && now - lastFaceDetectionTimeRef.current < 2000) {
        return
      }

      if (isProcessingRef.current) {
        return
      }

      if (!faceDetectorRef.current || !videoEle) {
        return
      }

      if (!mediaPipeReadyRef.current) {
        return
      }

      if (
        videoEle.readyState < 2 ||
        videoEle.videoWidth === 0 ||
        videoEle.videoHeight === 0
      ) {
        return
      }

      isProcessingRef.current = true
      lastFaceDetectionTimeRef.current = now

      try {
        const videoWidth = videoEle.videoWidth
        const videoHeight = videoEle.videoHeight

        if (videoWidth === 0 || videoHeight === 0) {
          return
        }

        const startTimeMs = performance.now()
        const results = faceDetectorRef.current.detectForVideo(
          videoEle,
          startTimeMs
        )

        if (results?.detections?.length > 0) {
          if (!showCalibration) {
            noFaceDetectedCount.current = 0

            setCalibrationModal((prev) => {
              if (prev.open) {
                return { ...prev, title: null, open: false }
              }
              return prev
            })
          }

          const detection = results.detections[0]
          const keypoints = detection.keypoints
          const boundingBox = detection.boundingBox

          const yawValue = estimateYaw(
            keypoints,
            boundingBox,
            videoWidth,
            videoHeight
          )
          const rollValue = estimateRoll(keypoints)
          const pitchValue = 0

          const centered = isFaceCentered(boundingBox, videoWidth, videoHeight)

          if (showCalibration) {
            const updates = {
              inRoll: rollValue,
              inPitch: pitchValue,
              inYaw: yawValue
            }

            if (
              centered &&
              rollValue < ROLL_THRESHOLD &&
              rollValue > -ROLL_THRESHOLD &&
              yawValue < YAW_THRESHOLD &&
              yawValue > -YAW_THRESHOLD &&
              results.detections.length === 1
            ) {
              goodFrameCountRef.current++
              if (goodFrameCountRef.current >= CALIBRATION_FRAMES_NEEDED) {
                updates.data = [rollValue, pitchValue, yawValue]
                setCalibrationModal((prev) => ({ ...prev, showButton: true }))

                // Capture frame and call API immediately — no render-cycle delay
                handleCalibrateCamera({
                  rollValue,
                  pitchValue,
                  yawValue,
                  videoElement: videoEle,
                  overrideContestId
                })
              }
            } else {
              goodFrameCountRef.current = 0
              setCalibrationModal((prev) => ({
                ...prev,
                showButton: false
              }))
            }

            setMainFrame((prev) => ({ ...prev, ...updates }))
          }
        } else if (!showCalibration) {
          if (
            noFaceDetectedCount.current <
            NO_FACE_DETECTION_BLOCK_UI_IN_MINS * 60
          ) {
            noFaceDetectedCount.current = noFaceDetectedCount.current + 5
          } else {
            setCalibrationModal((prev) => {
              if (!prev.open) {
                return { ...prev, open: true, title: 'No Face Detected' }
              }
              return prev
            })
          }
          setMainFrame({ data: [], inPitch: null, inRoll: null, inYaw: null })
          smoothedYawRef.current = null
          smoothedRollRef.current = null
        } else {
          setMainFrame({ data: [], inPitch: null, inRoll: null, inYaw: null })
          goodFrameCountRef.current = 0
          smoothedYawRef.current = null
          smoothedRollRef.current = null
        }
      } catch (error) {
        console.error('Error in predictWebcam:', error)
      } finally {
        isProcessingRef.current = false
      }
    },
    [estimateYaw, estimateRoll, isFaceCentered, handleCalibrateCamera]
  )

  const cleanUpResource = useCallback(() => {
    const stopMediaStream = (stream) => {
      try {
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach((t) => {
            try {
              t.stop()
            } catch (e) {}
          })
        }
      } catch (e) {}
    }

    try {
      stopMediaStream(videoRef.current)

      const tryClose = (obj) => {
        if (!obj) return
        try {
          if (typeof obj.close === 'function') obj.close()
          else if (typeof obj.dispose === 'function') obj.dispose()
          else if (typeof obj.stop === 'function') obj.stop()
        } catch (e) {}
      }

      tryClose(faceDetectorRef.current)
      faceDetectorRef.current = null
      mediaPipeReadyRef.current = false
      setIsMediaPipeReadyState(false)

      const clearCanvas = (canvasRef) => {
        try {
          if (!canvasRef.current) return
          const ctx = canvasRef.current.getContext?.('2d')
          if (ctx)
            ctx.clearRect(
              0,
              0,
              canvasRef.current.width,
              canvasRef.current.height
            )
          canvasRef.current.width = 0
          canvasRef.current.height = 0
        } catch (e) {}
      }

      clearCanvas(hiddenCanvasRef)

      videoRef.current = null
      isScriptLoaded.current = false
      setScriptLoaded({ cvLoaded: true, mediaPipeLoaded: false })
    } catch (error) {
      console.error('Error during cleanUpResource:', error)
    } finally {
      noFaceDetectedCount.current = 0
      lastFaceDetectionTimeRef.current = 0
      isProcessingRef.current = false
      goodFrameCountRef.current = 0
      smoothedYawRef.current = null
      smoothedRollRef.current = null
      calibrationInProgressRef.current = false
    }
  }, [setScriptLoaded])

  return (
    <MediapipeContext.Provider
      value={{
        scriptLoaded,
        predictWebcam,
        setShowCanvas,
        cleanUpResource,
        calibrationDetails,
        setCalibrationDetails,
        intialiseMediapipe,
        setCalibrationModal,
        intialiseVideoElement,
        isCalibrationModalOpen: calibrationModal.open,
        mainFrame,
        isMediaPipeReady: isMediaPipeReadyState,
        isMediaPipeLoading,
        calibrationModalState: calibrationModal
      }}
    >
      <>
        {children}

        {showCanvas?.webcam && (
          <canvas ref={hiddenCanvasRef} style={{ display: 'none' }} />
        )}

        <CalibrationModal
          video={videoRef.current}
          title={calibrationModal?.title}
          frameStatistics={mainFrame}
          open={calibrationModal.open}
          showButton={calibrationModal.showButton}
          isMediaPipeReady={mediaPipeReadyRef.current}
          isMediaPipeLoading={isMediaPipeLoading}
          handleClose={() => {
            setCalibrationModal({ open: false, title: null, showButton: false })
          }}
        />
      </>
    </MediapipeContext.Provider>
  )
}

export default MediapipeProvider
